// The pieces the Labor and Materials tabs share (docs/job-view-brief.md section 5; the drawings are
// docs/screens/job-view-screens-round6-labor.html f1 and round7-materials.html f1, the head from round 5 f2 and f3):
// the tab head with the Build control and the grouping picker, the one jump control, and a few small helpers.

import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Resolution, ResolvedItem } from '@zerogap/engine';
import { pricedUnder } from '../../../lib/job';
import type { Row } from '../../../lib/api';
import { jumpTo, type JobCtx, type TabKey } from '../Workspace';

/** The arrow the jump control carries, one colour. */
export function Arrow() {
  return (
    <svg viewBox="0 0 12 12" aria-hidden="true">
      <path d="M2.5 9.5 9.5 2.5M4.5 2.5h5v5" />
    </svg>
  );
}

/**
 * The jump to a lowest line's home (section 5): at rest the home in two words ("L-03 · route"), the title naming the
 * whole landing. A link, never a second place to edit.
 */
export function Jump({ ctx, home, full, to, back }: { ctx: JobCtx; home: string; full: string; to: { tab: TabKey; item?: string | null; lit: string }; back: TabKey }) {
  return (
    <button
      type="button"
      className="jmp"
      title={`Opens ${full}, lit · Back to ${back === 'labor' ? 'Labor' : 'Materials'} brings you here`}
      aria-label={`Change at ${full}`}
      onClick={(e) => {
        e.stopPropagation();
        jumpTo(ctx, { ...to, back });
      }}
    >
      <Arrow />
      <span>{home}</span>
    </button>
  );
}

/** The jump on a pooled catalog material: that material in the Library (Spencer, round 7), for a login with library.open_from_job. */
export function LibraryJump({ materialId, name }: { materialId: string; name: string }) {
  return (
    <Link className="jmp" to={`/library/materials?row=${materialId}`} title={`Opens ${name} in the Library, lit`} aria-label={`Open ${name} in the Library`} onClick={(e) => e.stopPropagation()}>
      <Arrow />
      <span>Library</span>
    </Link>
  );
}

/** A choice kept for the browser session, so Back to Labor reopens the tab grouped as it was left. */
export function useChoice<T extends string>(scope: string, start: T, allowed: readonly T[]): [T, (v: T) => void] {
  const read = (): T => {
    try {
      const v = sessionStorage.getItem(`zg.choice.${scope}`) as T | null;
      return v && allowed.includes(v) ? v : start;
    } catch {
      return start;
    }
  };
  const [v, setV] = useState<T>(read);
  const put = (next: T) => {
    setV(next);
    try {
      sessionStorage.setItem(`zg.choice.${scope}`, next);
    } catch {
      /* no session storage: the choice simply starts over */
    }
  };
  return [v, put];
}

/** Every Build of the job priced with the add-ons on, the one the tabs price under read from the context. */
export function useBuildPricings(ctx: JobCtx): { build: Row; res: Resolution | null }[] {
  // the base first, then the others in their order
  const builds = [...ctx.data.builds].sort((a, b) => Number(!!b.is_base) - Number(!!a.is_base));
  const addOns = ctx.data.addOns.filter((a) => ctx.addOnsOn.includes(a.id as string));
  return useMemo(
    () => builds.map((b) => ({ build: b, res: ctx.build && b.id === ctx.build.id ? ctx.r.res : pricedUnder(ctx.asEntered, b, addOns) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [builds, ctx.asEntered, ctx.r.res, ctx.build?.id, ctx.addOnsOn.join(',')],
  );
}

/**
 * The tab head (round 5 f2 and f3): the Build control, each button the build's name and its figure; the grouping
 * picker; the totals at the right.
 */
export function TabHead<G extends string>({ ctx, figure, groups, group, onGroup, totals }: { ctx: JobCtx; figure: (res: Resolution) => string; groups: { key: G; label: string }[]; group: G; onGroup: (g: G) => void; totals: ReactNode }) {
  const priced = useBuildPricings(ctx);
  return (
    <div className="tabhead">
      {priced.length > 0 && (
        <div className="f">
          <span className="lbl">Build</span>
          <div className="seg bsel" role="group" aria-label="Build">
            {priced.map(({ build, res }) => (
              <button key={build.id} type="button" className={ctx.build?.id === build.id ? 'on' : ''} aria-pressed={ctx.build?.id === build.id} title={build.is_base ? `${build.name} · the base` : (build.name as string)} onClick={() => ctx.go({ build: build.id as string })}>
                {build.name as string}
                <span className="ct">{res ? figure(res) : '…'}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="f">
        <span className="lbl">Grouped</span>
        <div className="seg sm" role="group" aria-label="Grouping">
          {groups.map((g) => (
            <button key={g.key} type="button" className={g.key === group ? 'on' : ''} aria-pressed={g.key === group} onClick={() => onGroup(g.key)}>
              {g.label}
            </button>
          ))}
        </div>
      </div>
      <span className="sp" />
      <div className="tots">{totals}</div>
    </div>
  );
}

/** One of the head's totals: the figure, and what it is. `sub` is the small kind. */
export function Tot({ value, label, sub }: { value: ReactNode; label: string; sub?: boolean }) {
  return (
    <div className={sub ? 'sub' : undefined}>
      <b>{value}</b>
      <span>{label}</span>
    </div>
  );
}

/** Whole cents shared out by weights, largest remainder, so the parts add up to the whole exactly. */
export function allocate(total: number, weights: number[]): number[] {
  const sum = weights.reduce((n, w) => n + Math.max(0, w), 0);
  if (weights.length === 0) return [];
  if (sum <= 0) return weights.map((_, i) => (i === 0 ? total : 0));
  const raw = weights.map((w) => (total * Math.max(0, w)) / sum);
  const out = raw.map((x) => Math.floor(x));
  let left = total - out.reduce((n, x) => n + x, 0);
  const order = raw.map((x, i) => ({ i, r: x - Math.floor(x) })).sort((a, b) => b.r - a.r || a.i - b.i);
  for (let k = 0; left > 0 && k < order.length; k++, left--) out[order[k]!.i]! += 1;
  return out;
}

/** Items in the order of their numbers, L-2 before L-10. */
export function byNumber(a: Pick<ResolvedItem, 'number' | 'sort'>, b: Pick<ResolvedItem, 'number' | 'sort'>): number {
  return a.number.localeCompare(b.number, undefined, { numeric: true }) || a.sort - b.sort;
}

/** Hours from minutes: to one place, to two under a tenth of an hour (an item's share of a short step). */
export function h1(minutes: number | string): string {
  const m = Number(minutes ?? 0);
  return `${(m / 60).toFixed(m > 0 && m < 6 ? 2 : 1)} h`;
}
